import { useEffect, useCallback } from "react";
import { observer } from "mobx-react-lite";
import { Button, Chip, Table } from "@heroui/react";
import { contractorDetailStore } from "@/stores/ContractorDetailStore";
import { PAYOUT_STATUS_LABEL, PAYOUT_STATUS_COLOR } from "../constants";
import { SortPanel, SortableColumnHeader } from "./shared";
import { DateRangeFilter } from "@/shared/ui/DateRangeFilter";
import { LoadMoreSentinel } from "@/shared/ui/LoadMoreSentinel";
import { useSortPanel } from "@/hooks/useSortPanel";
import { formatMoneyRub } from "@shared-utils/helpers";

const PO_SORT_COLUMNS = [
  { id: "date", label: "Дата" },
  { id: "amount", label: "Сумма" },
];

const PayoutsTab: React.FC = observer(() => {
  const store = contractorDetailStore;
  const sort = useSortPanel("date");

  const handleLoadMore = useCallback(() => {
    store.po.loadMore();
  }, [store]);

  useEffect(() => {
    store.po.load();
  }, []);

  const lastPayoutFormatted = store.po.lastPayoutDate
    ? new Date(store.po.lastPayoutDate).toLocaleDateString("ru-RU")
    : "—";

  const handleDateRange = (range: { start: string; end: string } | null) => {
    let endDate = range?.end ?? "";
    if (endDate) {
      const d = new Date(endDate);
      d.setDate(d.getDate() + 1);
      endDate = d.toISOString().slice(0, 10);
    }
    store.po.setDateRange(range?.start ?? "", endDate);
    store.po.load(1);
  };

  return (
    <div className="scd-tab-content scd-tab-content--scroll">
      {/* Pending balance card */}
      <div className="scd-payout-balance">
        <div className="scd-payout-balance__info">
          <span className="scd-payout-balance__label">К выплате</span>
          <span className="scd-payout-balance__value">
            {formatMoneyRub(store.po.pendingPayout)}
          </span>
          <span className="scd-payout-balance__hint">
            Накоплено с последней выплаты {lastPayoutFormatted}
          </span>
        </div>
        <Button
          variant="primary"
          className="scd-payout-balance__btn"
          isDisabled={store.po.pendingPayout <= 0}
          onPress={() => store.po.openModal()}
        >
          Выплатить
        </Button>
      </div>

      {/* Toolbar */}
      <div className="scd-toolbar">
        <div className="scd-toolbar__left">
          <DateRangeFilter label="Период выплат" onChange={handleDateRange} />
        </div>
        <div className="scd-toolbar__right">
          <SortPanel
            columns={PO_SORT_COLUMNS}
            sortDescriptor={sort.sortDescriptor}
            setSortDescriptor={sort.setSortDescriptor}
            isSortOpen={sort.isSortOpen}
            setIsSortOpen={sort.setIsSortOpen}
            sortAnchorRef={sort.sortAnchorRef}
            sortPanelRef={sort.sortPanelRef}
          />
        </div>
      </div>

      {/* Table */}
      <div className="scd-table-wrap">
        <Table>
          <Table.ScrollContainer>
            <Table.Content
              aria-label="Выплаты"
              className="scd-table"
              style={{ minWidth: "480px" }}
              sortDescriptor={sort.sortDescriptor}
              onSortChange={(d) =>
                sort.setSortDescriptor({
                  column: String(d.column),
                  direction: d.direction as "ascending" | "descending",
                })
              }
            >
              <Table.Header className="scd-table__head scd-table__head--sticky">
                <Table.Column
                  id="date"
                  className="scd-table__th"
                  style={{ width: "200px" }}
                  isRowHeader
                  allowsSorting
                >
                  {({ sortDirection }) => (
                    <SortableColumnHeader sortDirection={sortDirection}>
                      Дата / время
                    </SortableColumnHeader>
                  )}
                </Table.Column>
                <Table.Column
                  id="amount"
                  className="scd-table__th scd-table__th_right"
                  style={{ width: "160px" }}
                  allowsSorting
                >
                  {({ sortDirection }) => (
                    <SortableColumnHeader sortDirection={sortDirection}>
                      Сумма
                    </SortableColumnHeader>
                  )}
                </Table.Column>
                <Table.Column
                  id="status"
                  className="scd-table__th"
                  style={{ width: "140px" }}
                >
                  Статус
                </Table.Column>
              </Table.Header>
              <Table.Body className="scd-table__body">
                {store.po.items.length === 0 ? (
                  <Table.Row className="scd-table__row">
                    <Table.Cell
                      className="scd-table__td"
                      colSpan={3}
                      style={{
                        textAlign: "center",
                        color: "var(--color-gray-500)",
                      }}
                    >
                      {store.po.isLoading ? "Загрузка..." : "Нет выплат"}
                    </Table.Cell>
                  </Table.Row>
                ) : (
                  store.po.items.map((p) => (
                    <Table.Row
                      key={p.uuid}
                      id={p.uuid}
                      className="scd-table__row"
                    >
                      <Table.Cell className="scd-table__td scd-table__td--date">
                        {new Date(p.date).toLocaleString("ru-RU", {
                          day: "2-digit",
                          month: "2-digit",
                          year: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </Table.Cell>
                      <Table.Cell className="scd-table__td scd-table__td_right scd-amount--negative">
                        {formatMoneyRub(p.amount)}
                      </Table.Cell>
                      <Table.Cell className="scd-table__td">
                        <Chip
                          color={PAYOUT_STATUS_COLOR[p.status] ?? "default"}
                          variant="soft"
                          size="sm"
                        >
                          {PAYOUT_STATUS_LABEL[p.status] ?? p.status}
                        </Chip>
                      </Table.Cell>
                    </Table.Row>
                  ))
                )}
              </Table.Body>
            </Table.Content>
          </Table.ScrollContainer>
        </Table>
        <LoadMoreSentinel
          onLoadMore={handleLoadMore}
          isLoading={store.po.isLoading}
          hasMore={store.po.meta.hasNextPage}
        />
      </div>
    </div>
  );
});

PayoutsTab.displayName = "PayoutsTab";
export default PayoutsTab;
